import {useContext, useState} from 'react';
import {FlatList, StyleSheet, View} from 'react-native';
import {
  Button,
  Caption,
  Headline,
  IconButton,
  List,
  Subheading,
  Surface,
  Text,
  Title,
} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore from '@react-native-firebase/firestore';

import {UserContext} from '../App';
import {useAuthPersonInfo} from '../hooks/usePersonInfo';
import {
  filterSubscribers,
  LimitedPartner,
  Subscriber,
  useFetchLimitedPartners,
  useSubscribers,
} from '../hooks/useDomainData';
import {LimitedPartnerComponent} from './LimitedPartners';
import {useAppSettings} from '../components/AppSettings';
import {SubscriberEditor} from '../components/FormikStoreEditors/SubscriberEditor';

export function SubscriberComponent(props: {
  subscriber: Subscriber;
  alwaysOpen?: boolean;
}): JSX.Element {
  const limitedPartners = useFetchLimitedPartners();
  const person = useAuthPersonInfo();
  const appSettings = useAppSettings();
  const queryClient = useQueryClient();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentFirestoreObject, setCurrentFirestoreObject] = useState<
    Subscriber | undefined
  >();

  const collectionName = 'subscribers';
  const handleDeleteFirestoreObject = useMutation(
    (firestoreObject: Subscriber) => {
      console.log('should delete: ' + firestoreObject.ref.path);
      return firestore()
        .collection(collectionName)
        .doc(firestoreObject.ref.id)
        .delete();
    },
    {
      onSettled: () => {
        console.log('deleting succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
    },
  );

  const handleEditFirestoreObject = (firestoreObject?: Subscriber) => {
    console.log('open for edit or new object: ' + firestoreObject?.ref.path);
    setCurrentFirestoreObject(firestoreObject);
    setEditDialogVisible(true);
  };

  return (
    <>
      <View key={props.subscriber.subscriberId}>
        {!props.alwaysOpen && (
          <List.Accordion
            title={`${props.subscriber.fund?.fundCode} Investor: ${props.subscriber.subscriberName}`}>
            <View style={styles.horizontalEnd}>
              {(person?.isAdmin || person?.isGuestRelations) && (
                <>
                  <Text>Subscriber Actions: </Text>
                  <IconButton
                    icon="pencil"
                    size={18}
                    color={appSettings.currentTheme.colors.primary}
                    onPress={() => handleEditFirestoreObject(props.subscriber)}
                  />
                </>
              )}
              {person?.isAdmin && (
                <IconButton
                  icon="delete"
                  size={18}
                  color={appSettings.currentTheme.colors.primary}
                  onPress={() =>
                    handleDeleteFirestoreObject.mutate(props.subscriber)
                  }
                />
              )}
            </View>
            <View style={styles.content}>
              <SubscriberLimitedPartners
                limitedPartners={props.subscriber.limitedPartners}
              />
            </View>
          </List.Accordion>
        )}
        {props.alwaysOpen && (
          <>
            <View style={styles.content}>
              <View style={styles.horizontalSpaced}>
                <Subheading>
                  {props.subscriber.fund?.fundCode} Investor:{' '}
                  {props.subscriber.subscriberName}
                </Subheading>
                <View style={styles.horizontalEnd}>
                  {(person?.isAdmin || person?.isGuestRelations) && (
                    <>
                      <Text>Subscriber Actions: </Text>
                      <IconButton
                        icon="pencil"
                        size={18}
                        color={appSettings.currentTheme.colors.primary}
                        onPress={() =>
                          handleEditFirestoreObject(props.subscriber)
                        }
                      />
                    </>
                  )}
                  {person?.isAdmin && (
                    <IconButton
                      icon="delete"
                      size={18}
                      color={appSettings.currentTheme.colors.primary}
                      onPress={() =>
                        handleDeleteFirestoreObject.mutate(props.subscriber)
                      }
                    />
                  )}
                </View>
              </View>
              <SubscriberLimitedPartners
                limitedPartners={props.subscriber.limitedPartners}
              />
            </View>
          </>
        )}
      </View>
      <SubscriberEditor
        dialogVisible={editDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setEditDialogVisible(visible);
          if (!visible) {
            setCurrentFirestoreObject(undefined);
          }
        }}
        subscriber={currentFirestoreObject}
        allLimitedPartners={limitedPartners}
      />
    </>
  );
}

function SubscriberLimitedPartners(props: {
  limitedPartners?: LimitedPartner[];
}): JSX.Element {
  // console.log(
  //   'rendering SubscriberLimitedPartners with count: ' +
  //     props.limitedPartners?.length,
  // );
  return (
    <View>
      {!props.limitedPartners && <Caption>Loading limited partners...</Caption>}
      {props.limitedPartners &&
        props.limitedPartners.map(lp => (
          <LimitedPartnerComponent
            key={lp.limitedPartnerId}
            limitedPartner={lp}
          />
        ))}
    </View>
  );
}

function Subscribers() {
  // const theme = useTheme();
  const user = useContext(UserContext);
  let subscribers = useSubscribers();
  const limitedPartners = useFetchLimitedPartners();
  const person = useAuthPersonInfo();
  let loading = false;

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentFirestoreObject, setCurrentFirestoreObject] = useState<
    Subscriber | undefined
  >();

  const handleEditFirestoreObject = (firestoreObject?: Subscriber) => {
    console.log('open for edit or new object: ' + firestoreObject?.ref.path);
    setCurrentFirestoreObject(firestoreObject);
    setEditDialogVisible(true);
  };

  if (!user) {
    return null;
  }

  if (!person) {
    loading = true;
  }

  if (!person?.isAdmin && !person?.isGuestRelations) {
    subscribers = filterSubscribers(subscribers);
  }

  const renderItem = (props: {item: Subscriber}): JSX.Element => {
    return (
      <Surface style={styles.focusedSurface} key={props.item.subscriberId}>
        <SubscriberComponent subscriber={props.item} />
      </Surface>
    );
  };

  return (
    <>
      <View style={styles.horizontalCentered}>
        <Title style={styles.title}>Subscribers</Title>
        {(person?.isAdmin || person?.isGuestRelations) && (
          <Button mode="outlined" onPress={() => handleEditFirestoreObject()}>
            Create Subscriber
          </Button>
        )}
      </View>
      <FlatList
        data={subscribers}
        // @ts-ignore
        renderItem={renderItem}
        keyExtractor={item => item.subscriberId + ''}
        style={[styles.content]}>
        <Headline style={styles.centerContent}>Subscribers</Headline>
        {(!subscribers || loading) && <Caption>loading...</Caption>}
        {subscribers &&
          !loading &&
          subscribers.map(sub => (
            <Surface style={styles.focusedSurface} key={sub.subscriberId}>
              <SubscriberComponent subscriber={sub} />
            </Surface>
          ))}
      </FlatList>
      <SubscriberEditor
        dialogVisible={editDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setEditDialogVisible(visible);
          if (!visible) {
            setCurrentFirestoreObject(undefined);
          }
        }}
        subscriber={currentFirestoreObject}
        allLimitedPartners={limitedPartners}
      />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
  },
  content: {
    padding: 15,
  },
  centerContent: {
    justifyContent: 'center',
  },
  center: {
    width: '100%',
    alignItems: 'center',
  },
  focusedSurface: {
    padding: 10,
    margin: 10,
    elevation: 5,
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalSpaced: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  horizontalEnd: {
    flex: 1,
    flexDirection: 'row',
    paddingHorizontal: 10,
    justifyContent: 'flex-end',
    alignSelf: 'flex-end',
    alignItems: 'center',
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    paddingHorizontal: 10,
  },
});

export default Subscribers;
